"use client";
import { usePathname } from "next/navigation";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { UmbrellaPromoPageTestResult } from "@/monarch/rules/umbrellaPromoPage/types";
import type { HeroProps } from "../types";
import Hero from "..";

type ValidOffer = {
  discount: string;
  label: string;
  href: string;
};

const isValidOffer = (offer: unknown): offer is ValidOffer =>
  !!(
    offer &&
    typeof offer === "object" &&
    "discount" in offer &&
    "label" in offer &&
    "href" in offer &&
    !!offer.discount &&
    !!offer.label &&
    !!offer.href
  );

const getValidOffers = (offers: unknown[]): ValidOffer[] =>
  offers.filter(isValidOffer).slice(0, 2);

interface MonarchHeroWrapperProps extends HeroProps {
  shortcodeOffers?: Array<{
    discount: string;
    label: string;
    href: string;
  }>;
}

/**
 * Wrapper component that integrates Hero with Monarch for offers control.
 * When on /offers page with no-image variant, uses Monarch data to override offers.
 */
export default function MonarchHeroWrapper({
  shortcodeOffers = [],
  ...heroProps
}: MonarchHeroWrapperProps) {
  const pathname = usePathname();
  const isOffersPage = pathname === "/offers" || pathname?.endsWith("/offers");
  const isNoImageVariant = heroProps.variant === "no-image";

  // Only use Monarch on /offers page with no-image variant
  if (!isOffersPage || !isNoImageVariant) {
    return (
      <Hero
        {...heroProps}
        {...(shortcodeOffers.length > 0 && { offers: shortcodeOffers })}
      />
    );
  }

  const renderHeroWithMonarch = (
    testResult: UmbrellaPromoPageTestResult | null,
  ) => {
    // Start with shortcode props as defaults
    let finalTitle = heroProps.title;
    let finalTitleOverride = heroProps.titleOverride;
    let finalChildren = heroProps.children;
    let finalOffers = shortcodeOffers;

    // Use Monarch hero data if available
    if (testResult?.data?.hero) {
      const monarchHero = testResult.data.hero;

      // Use header as titleOverride (or title if titleOverride not set)
      if (monarchHero.header) {
        finalTitleOverride = monarchHero.header;
      }

      // Use subheader as children content
      if (monarchHero.subheader) {
        finalChildren = <p>{monarchHero.subheader}</p>;
      }

      // Use offers from hero.offers if available
      if (monarchHero.offers && Array.isArray(monarchHero.offers)) {
        const validMonarchOffers = getValidOffers(monarchHero.offers);
        if (validMonarchOffers.length > 0) {
          finalOffers = validMonarchOffers;
        }
      }
    }

    // Fallback to legacy offers structure if hero.offers not available
    if (
      finalOffers.length === 0 &&
      testResult?.data?.offers &&
      Array.isArray(testResult.data.offers)
    ) {
      const validMonarchOffers = getValidOffers(testResult.data.offers);
      if (validMonarchOffers.length > 0) {
        finalOffers = validMonarchOffers;
      }
    }

    return (
      <Hero
        {...heroProps}
        title={finalTitle}
        titleOverride={finalTitleOverride}
        {...(finalOffers.length > 0 && { offers: finalOffers })}
      >
        {finalChildren}
      </Hero>
    );
  };

  const monarchHeroComponent = (
    <MonarchDataConsumer<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      render={(testResult) => renderHeroWithMonarch(testResult || null)}
    />
  );

  return (
    <MonarchResolver<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      Loader={<Hero {...heroProps} />}
      variants={{
        control: monarchHeroComponent,
        test: monarchHeroComponent,
      }}
      fallbackVariant="control"
    />
  );
}
